import { useMemo } from "react";

import { Link, useParams, useSearch } from "@tanstack/react-router";
import type { AgChartOptions } from "ag-charts-community";
import { ArrowLeft } from "lucide-react";

import {
  AgChartPanel,
  buildAgOptions,
  useIsDark,
  type ChartDataset,
} from "@/components/cinch/charts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useAeDetail } from "../api";
import { CroForecastFilterBar } from "../components/FilterBar";
import { CroForecastSubNav } from "../components/SubNav";
import { fmtCents, fmtMonthName, fmtPct } from "../format";
import { resolveSearch, type CroForecastSearch } from "../search";

export function CroForecastAeDetailPage() {
  const { repId } = useParams({ from: "/cro-forecast/ae/$repId" });
  const search = useSearch({ from: "/cro-forecast/ae/$repId" }) as CroForecastSearch;
  const { year, week } = resolveSearch(search);
  const { data, isLoading, error } = useAeDetail(repId, year, week);
  const isDark = useIsDark();

  // Call trajectory as a proper line chart on the shared AG Charts panel —
  // brand-themed, real axes, hover tooltips. Values stay in cents and
  // ``valueFormat`` handles the display, so axis labels and tooltips agree with
  // every other money figure on the page.
  const trajectoryDataset = useMemo<ChartDataset>(
    () => ({
      key: "ae-call-trajectory",
      label: "Forecast call",
      kind: "timeseries",
      chartTypes: ["line"],
      categoryKey: "week",
      rows: (data?.trajectory ?? []).map((p) => ({
        week: `Wk ${p.iso_week}`,
        call_cents: p.cents,
      })),
      series: [{ key: "call_cents", name: "Forecast call" }],
      // Empty = fall back to the default brand palette order.
      colors: [],
      valueFormat: fmtCents,
    }),
    [data?.trajectory],
  );
  const trajectoryOptions = useMemo<AgChartOptions>(
    () => buildAgOptions(trajectoryDataset, "line", isDark),
    [trajectoryDataset, isDark],
  );

  return (
    <div className="flex flex-col">
      <CroForecastFilterBar search={search} />
      <CroForecastSubNav search={search} />

      <div className="flex flex-col gap-4 p-4 md:p-6">
        <Link
          to="/cro-forecast/forecast-meeting"
          search={search as Record<string, unknown>}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-power"
        >
          <ArrowLeft className="h-3 w-3" /> Back to Forecast Meeting
        </Link>

        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {error && (
          <p className="text-sm text-destructive">
            Failed: {(error as Error).message}
          </p>
        )}
        {data && (
          <>
            <header>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {data.tier}
              </p>
              <h1 className="text-2xl font-bold tracking-tight">
                {data.display_name}
              </h1>
              <p className="text-xs text-muted-foreground">
                {data.email ?? "—"} · HubSpot owner {data.hubspot_owner_id}
              </p>
            </header>

            {/* Monthly call + closed tiles */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {data.months.map((m) => (
                <Card key={m.month}>
                  <CardHeader className="pb-1">
                    <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
                      {fmtMonthName(m.month)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="font-mono text-xs text-muted-foreground">
                      Call
                    </p>
                    <p className="text-lg font-bold tabular-nums">
                      {fmtCents(m.call_cents)}
                    </p>
                    <p className="mt-1 font-mono text-xs text-muted-foreground">
                      Closed
                    </p>
                    <p className="text-base text-success tabular-nums">
                      {fmtCents(m.closed_cents)}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
                  Call trajectory — Q{data.quarter} {data.quarter_year}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.trajectory.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No calls recorded yet for {data.display_name} this quarter.
                  </p>
                ) : (
                  <AgChartPanel options={trajectoryOptions} className="h-64" />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
                  Override history (last 8 weeks)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.overrides.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No overrides recorded.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Week</TableHead>
                        <TableHead className="text-right">Override</TableHead>
                        <TableHead>Note</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.overrides.map((o) => (
                        <TableRow key={`${o.iso_year}-${o.iso_week}`}>
                          <TableCell className="font-mono text-xs">
                            {o.iso_year} Wk {o.iso_week}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {fmtPct(o.override_pct, 1)}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {o.note ?? "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">
                  Open deals ({data.open_deals.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.open_deals.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No open deals for this AE.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Deal</TableHead>
                        <TableHead>Stage</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">Probability</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.open_deals.map((d) => (
                        <TableRow key={d.hubspot_deal_id}>
                          <TableCell className="font-medium">{d.name}</TableCell>
                          <TableCell className="text-xs">{d.stage_name}</TableCell>
                          <TableCell className="font-mono text-xs text-muted-foreground">
                            {d.created_date ?? "—"}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {fmtCents(d.amount_cents)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs">
                            {d.commit_probability != null
                              ? `${Math.round(d.commit_probability * 100)}%`
                              : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

